import { useEffect, useMemo, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { formatCurrency } from '../data';
import {
  confirmCodPaid,
  confirmOrderByOrderId,
  deleteOrder,
  getOrders,
  getSignedProofUrl,
  getSignedProofUrlByPath,
  reviewPayment,
  updateFulfilmentStage,
  updateOrderStatus
} from '../services/adminWorkspaceService';
import OrderShareTools from './OrderShareTools';
import ProofViewer from './ProofViewer';

const {
  FiCheck,
  FiDownload,
  FiExternalLink,
  FiEye,
  FiMessageCircle,
  FiRefreshCw,
  FiSearch,
  FiTrash2,
  FiXCircle
} = FiIcons;

const fulfilmentStages = [
  ['processing', 'Processing — checking stock'],
  ['packed', 'Packed'],
  ['ready_for_pickup', 'Ready to be picked up'],
  ['picked_up', 'Item picked up'],
  ['out_for_delivery', 'Out for delivery'],
  ['delivered', 'Delivered']
];

function normalizeWhatsappNumber(phone) {
  const digits = String(phone || '').replace(/\D/g, '');
  if (digits.startsWith('0')) return `94${digits.slice(1)}`;
  return digits;
}

function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState('');
  const [filterTab, setFilterTab] = useState('all');
  const [state, setState] = useState('loading');
  const [message, setMessage] = useState('');
  const [confirmingId, setConfirmingId] = useState('');
  const [viewerProof, setViewerProof] = useState(null);
  const [viewerUrl, setViewerUrl] = useState('');
  const [viewerLoading, setViewerLoading] = useState(false);

  const load = () => {
    setState('loading');
    getOrders()
      .then((rows) => {
        setOrders(rows);
        setState('ready');
      })
      .catch(() => setState('error'));
  };

  useEffect(load, []);

  const filtered = useMemo(() => {
    let result = orders;
    if (filterTab === 'payment') {
      result = result.filter((order) =>
        order.payment_method !== 'cash_on_delivery' &&
        ['pending', 'proof_submitted', 'under_review'].includes(order.payment_status)
      );
    } else if (filterTab === 'active') {
      result = result.filter((order) =>
        !['delivered', 'cancelled', 'rejected'].includes(order.order_status)
      );
    } else if (filterTab === 'delivered') {
      result = result.filter((order) => order.order_status === 'delivered');
    } else if (filterTab === 'cancelled') {
      result = result.filter((order) =>
        ['cancelled', 'rejected'].includes(order.order_status)
      );
    }

    const term = search.trim().toLowerCase();
    if (!term) return result;
    return result.filter((order) =>
      [
        order.order_number,
        order.customer_name,
        order.customer_email,
        order.customer_phone,
        order.tracking_number
      ].some((value) => String(value || '').toLowerCase().includes(term))
    );
  }, [orders, search, filterTab]);

  const getProofUrl = async (proof) => {
    if (proof.storage_path) {
      return getSignedProofUrlByPath(proof.storage_path);
    }
    return getSignedProofUrl(proof.id);
  };

  const openProof = async (proof) => {
    try {
      let url;
      if (proof.storage_path) {
        url = await getSignedProofUrlByPath(proof.storage_path);
      } else {
        url = await getSignedProofUrl(proof.id);
      }
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      setMessage(error.message);
    }
  };

  const viewProof = async (proof) => {
    setViewerLoading(true);
    setViewerProof(proof);
    setViewerUrl('');
    try {
      const url = await getProofUrl(proof);
      setViewerUrl(url);
    } catch (error) {
      setMessage(error.message);
      setViewerProof(null);
    } finally {
      setViewerLoading(false);
    }
  };

  const downloadProof = async (proof) => {
    try {
      const url = await getProofUrl(proof);
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = proof.original_filename || 'payment-proof.jpg';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      setMessage(error.message);
    }
  };

  const closeViewer = () => {
    setViewerProof(null);
    setViewerUrl('');
  };

  const confirmPayment = async (order) => {
    if (!window.confirm(`Confirm payment for ${order.order_number}?`)) return;
    setConfirmingId(order.id);
    try {
      const payment = order.payment?.[0];
      if (payment?.id) {
        // Try the direct order confirmation first (works by order_id, no payment_id needed)
        try {
          await confirmOrderByOrderId(order.id);
        } catch {
          // Fallback to the older reviewPayment if the new function isn't available
          await reviewPayment(payment.id, 'approve');
        }
      } else {
        // No payment record loaded — try direct confirmation by order ID
        await confirmOrderByOrderId(order.id);
      }
      setMessage(`Payment confirmed for ${order.order_number}.`);
      load();
    } catch (error) {
      setMessage(error.message);
    } finally {
      setConfirmingId('');
    }
  };

  const confirmCodCollection = async (order) => {
    if (!window.confirm(`Driver confirmed cash collection for ${order.order_number}? Mark this COD order paid?`)) return;
    setConfirmingId(order.id);
    try {
      await confirmCodPaid(order.id);
      setMessage(`Cash on delivery payment marked paid for ${order.order_number}.`);
      load();
    } catch (error) {
      setMessage(error.message);
    } finally {
      setConfirmingId('');
    }
  };

  const changeStage = async (order, stage) => {
    try {
      await updateFulfilmentStage(order.id, stage);
      setMessage(`Order ${order.order_number} fulfilment status updated.`);
      load();
    } catch (error) {
      setMessage(error.message);
    }
  };

  const cancelOrder = async (order) => {
    if (!window.confirm(`Cancel order ${order.order_number}?`)) return;
    try {
      await updateOrderStatus(order.id, 'cancelled', 'Cancelled by administrator');
      setMessage(`Order ${order.order_number} was cancelled.`);
      load();
    } catch (error) {
      setMessage(error.message);
    }
  };

  const removeOrder = async (order) => {
    const confirmed = window.confirm(
      `Permanently delete order ${order.order_number} for ${order.customer_name}?\n\n` +
      'The order, its items, payment records and status history will be removed, ' +
      'and its customer tracking link will stop working. This cannot be undone.'
    );
    if (!confirmed) return;
    try {
      await deleteOrder(order.id);
      setMessage(`Order ${order.order_number} was deleted.`);
      load();
    } catch (error) {
      setMessage(error.message);
    }
  };

  const counts = {
    all: orders.length,
    payment: orders.filter((order) =>
      order.payment_method !== 'cash_on_delivery' &&
      ['pending', 'proof_submitted', 'under_review'].includes(order.payment_status)
    ).length,
    active: orders.filter((order) =>
      !['delivered', 'cancelled', 'rejected'].includes(order.order_status)
    ).length,
    delivered: orders.filter((order) => order.order_status === 'delivered').length,
    cancelled: orders.filter((order) =>
      ['cancelled', 'rejected'].includes(order.order_status)
    ).length
  };

  return (
    <div className="admin-overview-container">
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Orders</h1>
          <p className="admin-page-subtitle">
            Review payment proof, contact customers, and manage fulfilment.
          </p>
        </div>
      </div>

      <div className="orders-toolbar">
        <div className="orders-search-box">
          <SafeIcon icon={FiSearch} />
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search by name, phone, email, or order number..."
          />
        </div>
        <button className="orders-refresh-btn" onClick={load}>
          <SafeIcon icon={FiRefreshCw} />
          Refresh
        </button>
      </div>

      <div className="orders-filter-tabs">
        {[
          ['all', 'All Orders'],
          ['payment', 'Proof to Review'],
          ['active', 'In Progress'],
          ['delivered', 'Delivered'],
          ['cancelled', 'Cancelled']
        ].map(([id, label]) => (
          <button
            key={id}
            className={filterTab === id ? 'active' : ''}
            onClick={() => setFilterTab(id)}
          >
            {label} ({counts[id]})
          </button>
        ))}
      </div>

      {message && <div className="admin-alert info">{message}</div>}
      {state === 'loading' && <div className="admin-state">Loading orders…</div>}
      {state === 'error' && (
        <div className="admin-state admin-error">Orders could not be loaded.</div>
      )}
      {state === 'ready' && filtered.length === 0 && (
        <div className="admin-state">No matching orders found.</div>
      )}

      <div className="orders-cards-grid zevi-order-list">
        {filtered.map((order) => {
          const payment = order.payment?.[0];
          const proofs = payment?.proofs?.filter(
            (proof) => proof.upload_status === 'uploaded'
          ) || [];
          const cancelled = ['cancelled', 'rejected'].includes(order.order_status);
          const customerPhone = order.customer_phone || '';
          const whatsapp = normalizeWhatsappNumber(customerPhone);
          const isNonCOD = order.payment_method !== 'cash_on_delivery';
          const awaitingReview = isNonCOD &&
            ['proof_submitted', 'under_review'].includes(order.payment_status);
          const pendingProof = isNonCOD &&
            order.payment_status === 'pending';
          const isConfirming = confirmingId === order.id;

          return (
            <article className="order-card" key={order.id}>
              <div className="order-card-top">
                <div className="order-card-top-left">
                  <span className="order-number-badge">{order.order_number}</span>
                  <span className="order-date-text">
                    {new Date(order.created_at).toLocaleString()}
                  </span>
                </div>
                <span className="order-amount-badge">
                  {formatCurrency(order.grand_total)}
                </span>
              </div>

              <div className="order-card-body">
                <div className="order-info-cell">
                  <label>Customer</label>
                  <span>{order.customer_name}</span>
                </div>
                <div className="order-info-cell">
                  <label>Phone</label>
                  {customerPhone ? (
                    <a href={`tel:${customerPhone}`}>{customerPhone}</a>
                  ) : (
                    <span>Not on file</span>
                  )}
                </div>
                <div className="order-info-cell">
                  <label>Email</label>
                  <a href={`mailto:${order.customer_email}`}>
                    {order.customer_email}
                  </a>
                </div>
                <div className="order-info-cell">
                  <label>Payment</label>
                  <span className="pay-method-tag">
                    {order.payment_method === 'cash_on_delivery'
                      ? 'Cash on Delivery'
                      : order.payment_method === 'bank_transfer'
                        ? 'Bank Transfer'
                        : 'QR Payment'}
                  </span>
                </div>
              </div>
              <div className="zevi-order-summary">
                <span>Order: {order.order_status.replaceAll('_', ' ')}</span>
                <span>Payment: {order.payment_status.replaceAll('_', ' ')}</span>
                <span>Fulfilment: {(order.fulfilment_stage || 'processing').replaceAll('_', ' ')}</span>
              </div>

              <div className="admin-payment-actions" style={{ padding: '0 22px 18px' }}>
                {proofs.map((proof) => (
                  <div className="proof-action-row" key={proof.id}>
                    <button
                      className="admin-view-proof-btn"
                      title="Check proof of payment screenshot"
                      onClick={() => viewProof(proof)}
                    >
                      <SafeIcon icon={FiEye} />
                      View Proof
                    </button>
                    <button
                      className="admin-download-proof-btn"
                      title="Download proof of payment"
                      onClick={() => downloadProof(proof)}
                    >
                      <SafeIcon icon={FiDownload} />
                      Download
                    </button>
                    <button
                      className="admin-view-proof-btn"
                      title="Open in new tab"
                      onClick={() => openProof(proof)}
                    >
                      <SafeIcon icon={FiExternalLink} />
                      Open in Tab
                    </button>
                  </div>
                ))}
                {awaitingReview && proofs.length === 0 && (
                  <span className="kpi-note" style={{ color: '#b8860b' }}>
                    Payment proof submitted — loading…
                  </span>
                )}
                {pendingProof && proofs.length === 0 && (
                  <span className="kpi-note" style={{ color: '#b8860b' }}>
                    Awaiting payment proof — customer may still upload it, or confirm manually if received offline.
                  </span>
                )}
                {awaitingReview && (
                  <button
                    className="admin-approve-btn"
                    onClick={() => confirmPayment(order)}
                    disabled={isConfirming}
                  >
                    <SafeIcon icon={FiCheck} />
                    {isConfirming ? 'Confirming…' : 'Confirm Payment & Order'}
                  </button>
                )}
                {pendingProof && (
                  <button
                    className="admin-approve-btn"
                    onClick={() => confirmPayment(order)}
                    disabled={isConfirming}
                    style={{ opacity: 0.85 }}
                  >
                    <SafeIcon icon={FiCheck} />
                    {isConfirming ? 'Confirming…' : 'Confirm Payment (no proof on file)'}
                  </button>
                )}
                {order.payment_method === 'cash_on_delivery' &&
                  order.payment_status === 'unpaid' &&
                  !cancelled &&
                  (order.order_status === 'delivered' || order.fulfilment_stage === 'delivered') && (
                    <button
                      className="admin-approve-btn"
                      onClick={() => confirmCodCollection(order)}
                      disabled={isConfirming}
                    >
                      <SafeIcon icon={FiCheck} />
                      {isConfirming ? 'Confirming…' : 'Mark COD as Paid'}
                    </button>
                  )}
                {isNonCOD && !awaitingReview && !pendingProof && proofs.length === 0 && (
                  <span className="kpi-note">No payment proof uploaded yet.</span>
                )}
              </div>

              <OrderShareTools
                order={order}
                whatsapp={whatsapp}
                onMessage={setMessage}
                onSaved={(text) => {
                  setMessage(text);
                  load();
                }}
              />

              <div className="order-card-footer">
                {!cancelled ? (
                  <select
                    className="order-status-select"
                    value={order.fulfilment_stage || 'processing'}
                    onChange={(event) => changeStage(order, event.target.value)}
                    disabled={
                      order.payment_method !== 'cash_on_delivery'
                      && !['verified', 'paid'].includes(order.payment_status)
                    }
                  >
                    {fulfilmentStages.map(([value, label]) => (
                      <option value={value} key={value}>{label}</option>
                    ))}
                  </select>
                ) : (
                  <strong className="status-pill archived">Order Cancelled</strong>
                )}

                <div className="order-quick-actions">
                  {whatsapp && (
                    <a
                      className="order-quick-btn confirm"
                      href={`https://wa.me/${whatsapp}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <SafeIcon icon={FiMessageCircle} />
                      WhatsApp
                    </a>
                  )}
                  {!cancelled && order.order_status !== 'delivered' && (
                    <button
                      className="admin-reject-btn"
                      onClick={() => cancelOrder(order)}
                    >
                      <SafeIcon icon={FiXCircle} />
                      Cancel Order
                    </button>
                  )}
                  <button
                    className="order-delete-btn"
                    onClick={() => removeOrder(order)}
                  >
                    <SafeIcon icon={FiTrash2} />
                    Delete
                  </button>
                </div>
              </div>
            </article>
          );
        })}
      </div>

      <ProofViewer
        isOpen={!!viewerProof}
        onClose={closeViewer}
        imageUrl={viewerUrl}
        fileName={viewerProof?.original_filename}
        loading={viewerLoading}
      />
    </div>
  );
}

export default AdminOrders;